/**
 * Service for fetching user permissions from the authorization API.
 * Separated from UserSessionService to isolate API concerns from storage concerns.
 */
export class UserPermissionService {
  /**
   * Fetch permission scopes for a user via GraphQL
   * @param baseIdentityUrl - Base URL of the identity server
   * @param profileId - The user's profile ID
   * @param customerId - The user's customer ID
   * @param csrfToken - CSRF token for the request
   * @returns Array of permission scope strings
   */
  static async fetchPermissions(
    authServiceUrl: string,
    profileId: string,
    customerId: string,
    csrfToken: string | null
  ): Promise<string[]> {
    const response = await fetch(`${authServiceUrl}/graphql`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF": csrfToken || "1",
      },
      body: JSON.stringify({
        query: `query {
          permissionsForUser(profileId: "${profileId}", customerId: "${customerId}") {
            permissionScope
          }
        }`,
      }),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch user permissions");
    }

    const result = await response.json();
    return Array.from(
      new Set(
        (result?.data?.permissionsForUser ?? []).map(
          (permission: { permissionScope: string }) =>
            permission.permissionScope
        )
      )
    );
  }
}
